"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import ReactionBtns from "../right-rail/reaction-btns/reaction-btns";
import { PrimaryBtn } from "../button/primary-button";
import {
  LearningPathDurationIcon,
  LearningPathLevelIcon,
  LearningPathLineIcon,
  LearningPathVideoIcon,
} from "../svgcomponents/lp-common-icons";
import styles from "./dynamicstyle.module.scss";
import { convertMinutesToHoursAndMinutes } from "@/utils/convert-to-hours-and-minutes";
import useUpdateLearningPath from "@/utils/update-learning-path";
import { formatDate } from "@/utils/convert-date";
import { useAuthStore, useCoursesStore } from "@/context/store";
import { useRouter } from "next/router";
import Subscribe from "../center-content/subscribe/subscribe";
import { hasUrlParameter } from "@/helpers/get-url-parameter";
import { useAppContext } from "@/context/appContext";
import useResponsive from "@/utils/hooks/Responsive-Screens";
import Tooltip from "../tooltip-custom/CustomTooltip";
import { TiltedPinIcon } from "../svgcomponents/common-icons";

// Debounce function to limit how often a function runs
const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

const DynamicStickyHeader = ({
  learningPathwayDetails,
  isLearningPathsDetail = false,
  created_at,
  term,
  courseDetails,
  handleCourseResume,
  isLearningPath,
  handleStartCourse,
  inCompletedLessons,
  setLoginModal,
  duration,
  isCompleted,
  level,
  lessonUid,
  isStandaloneCourse,
  nextSubmitFunction,
  uid,
  isCourse = false,
  isLesson,
  userData,
  redirectUrlPath,
  nextLessonFunction,
  lessonLoading,
  isTextLesson,
  isLast,
  isLastQuiz,
  close,
  setClose,
  isCourseDetailsPage = false,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [optimisticSubscribed, setOptimisticSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const headerRef = useRef(null);
  const originalTopRef = useRef(null);
  const scrollBuffer = 20;
  const lastScrollY = useRef(0);
  const duration_total = learningPathwayDetails
    ? convertMinutesToHoursAndMinutes(
        learningPathwayDetails?.duration_in_minutes
      )
    : duration;

  const { setShowLearningPathAlert } = useAppContext();
  const [loggedIn, setLoggedIn] = useState({});
  const { mutate } = useUpdateLearningPath(isLearningPath);
  const setPreviousPath = useAuthStore((state) => state.setPreviousPath);
  const courses = useCoursesStore((state) => state.courses);
  const router = useRouter();
  const courseUrl = router.query?.course;
  const { isMobile, isTablet } = useResponsive();
  useEffect(() => {
    let userData = localStorage.getItem("user");
    let parsedData;

    if (userData) {
      try {
        parsedData = JSON.parse(userData);
        if (parsedData?.email) {
          setLoggedIn(parsedData);
        } else {
          setLoggedIn(null);
        }
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
        setLoggedIn(null);
      }
    } else {
      setLoggedIn(null);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isLesson) {
      setIsMounted(true);
    }
  }, [isLesson]);

  const checkScroll = useCallback(() => {
    if (!headerRef.current || originalTopRef.current === null) return;

    const currentScrollY = window.scrollY;
    const threshold = originalTopRef.current;
    const scrollDirection =
      currentScrollY > lastScrollY.current ? "down" : "up";

    // Only update if we've scrolled past the buffer zone
    if (Math.abs(currentScrollY - threshold) > scrollBuffer) {
      if (scrollDirection === "down" && currentScrollY > threshold) {
        setIsSticky(true);
      } else if (scrollDirection === "up" && currentScrollY < threshold) {
        setIsSticky(false);
      }
    }

    lastScrollY.current = currentScrollY;
  }, []);

  const debouncedCheckScroll = useCallback(debounce(checkScroll, 10), [
    checkScroll,
  ]);

  useEffect(() => {
    // if (!isMounted || isLesson) return;

    const updateThreshold = () => {
      if (headerRef.current) {
        originalTopRef.current =
          headerRef.current.getBoundingClientRect().top + window.scrollY;
      }
    };

    updateThreshold();
    checkScroll();

    // Update threshold on resize
    window.addEventListener("resize", updateThreshold);
    window.addEventListener("scroll", debouncedCheckScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", updateThreshold);
      window.removeEventListener("scroll", debouncedCheckScroll);
    };
  }, [isMounted, isLesson, debouncedCheckScroll, checkScroll]);

  const handleButtonClick = async () => {
    const currentPath = location.pathname;
    setPreviousPath(currentPath);
    if (!loggedIn || !loggedIn.email) {
      if (typeof window !== "undefined") {
        localStorage.setItem("pendingEnrollment", currentPath);
      }
      setLoginModal(true);
      return;
    }
    let resumeUrl = "#";
    if (userData && (userData.isSubscribed || optimisticSubscribed)) {
      if (courses && Array.isArray(courses)) {
        const foundCourse = courses.find(
          (item) => item.uid === handleCourseResume
        );
        if (foundCourse) {
          resumeUrl =
            `/academy/learning-paths/${isLearningPath}/${foundCourse.url}`.replace(
              /\/+/g,
              "/"
            );
        } else if (handleCourseResume === uid) {
          router.push(`/learning-paths/${isLearningPath}/assessment`);
          resumeUrl = `/academy/learning-paths/${isLearningPath}/assessment`;
        }
      } else if (handleCourseResume === uid) {
        router.push(`/learning-paths/${isLearningPath}/assessment`);
        resumeUrl = `/academy/learning-paths/${isLearningPath}/assessment`;
      }
      resumeUrl.endsWith("assessment")
        ? router.push(`/learning-paths/${isLearningPath}/assessment`)
        : (window.location.href = resumeUrl);
    }

    if (!userData?.isSubscribed && !optimisticSubscribed) {
      if (!userData?.progress?.hasStarted) {
        handleStartCourse();
      } else {
        setIsEnrolling(true);
        setOptimisticSubscribed(true);
        setShowLearningPathAlert(true);
        try {
          mutate({ uid: uid, requestData: { isSubscribed: true } });
        } catch (error) {
          setOptimisticSubscribed(false);
          console.error("Failed to enroll:", error);
        } finally {
          setIsEnrolling(false);
        }
      }
    }
  };

  const handleBookmarkClick = async () => {
    if (!loggedIn || !loggedIn.email) {
      // setShowLoginModal(true);
      window.location.href = "/academy/login";
    }
    if (userData?.isBookmarked) {
      mutate({ uid: uid, requestData: { isBookmarked: false } });
    } else {
      mutate({ uid: uid, requestData: { isBookmarked: true } });
    }
  };
  const renderCTAButton = () => {
    if (isLoading) {
      return (
        <PrimaryBtn
          title="Loading..."
          disabled={true}
          customClass=" rounded-1"
        />
      );
    }

    if (!loggedIn?.email) {
      return (
        <PrimaryBtn
          title="Enroll Now"
          disabled={false}
          customClass="rounded-1"
          onClick={handleButtonClick}
        />
      );
    }

    if (!userData) {
      return (
        <PrimaryBtn
          title="Loading..."
          disabled={true}
          customClass=" rounded-1"
        />
      );
    }

    return (
      <PrimaryBtn
        title={
          !userData.isSubscribed && !optimisticSubscribed
            ? isEnrolling
              ? "Enrolling..."
              : "Enroll Now"
            : userData?.progress?.percentage > 0 && !isCompleted
              ? "Resume Learning"
              : "Start Learning"
        }
        // hasLink={loggedIn && (userData?.isSubscribed || optimisticSubscribed)}
        onClick={handleButtonClick}
        onkeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleButtonClick();
          }
        }}
        role="button"
        tabIndex={0}
        disabled={isEnrolling}
        customClass={`rounded-1`}
      />
    );
  };

  const headerStyles = {
    position: isMobile ? "relative" : "sticky",
    top: isMobile ? "0" : "68px",
    zIndex: isMobile ? "0" : "100",
    backgroundColor: isMobile ? "transparent" : "white",
    borderBottom: isSticky && !!isMobile ? "1px solid #DDE3EE" : "none",
    padding: isMobile ? "0px" : isCourse && !isLesson && "0px 0px 10px 0px",
    transition: "all 0.3s ease-in-out",
    margin: "0px",
    width: "100%",
    willChange: "transform, opacity",
    transform:
      (isSticky && !isTablet) || isMobile ? "translateY(0)" : "translateY(0)",
    opacity: 1,
  };
  return (
    <div
      ref={headerRef}
      className={`course-header ${isSticky ? "sticky-active" : ""}`}
      style={headerStyles}
    >
      {isMobile || isTablet ? (
        <>
          {close && !isCourseDetailsPage && (
            <div
              aria-label={`${close ? "Open" : "Close"} navigation menu`}
              role="button"
              className="outline-focussed"
              tabIndex={0}
            >
              <Tooltip
                text={`${close ? "Open" : "Close"} menu`}
                position="right"
              >
                <span
                  className="material-symbols-outlined"
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      document
                        .querySelector(".left-side-nav")
                        .classList.remove("overflowx-hidden");
                      document
                        .querySelector("iframe")
                        .classList.remove("text-lesson-iframe");
                    }
                    setClose(!close);
                  }}
                  role="button"
                >
                  expand_circle_right
                </span>
              </Tooltip>
            </div>
          )}
          <div
            className={styles.main_container}
            role="region"
            aria-label="Learning paths banner"
            tabIndex={0}
          >
            <div className={styles.title_container}>
              <h1 className={styles.title}>
                {learningPathwayDetails?.headline || courseDetails?.title}
              </h1>
              <div className="d-flex w-fit gap-2 flex-wrap align-items-center justify-content-start sm-justify-content-between fs-12 fw-normal text-gray ">
                {!learningPathwayDetails && term && (
                  <div className="d-flex w-fit gap-2 align-items-center justify-content-between fs-12 fw-normal text-gray ">
                    <div className="d-flex justify-content-center gap-1 align-items-center text-capitalize">
                      <LearningPathVideoIcon />
                      {isLesson ? "Lesson" : term}
                    </div>
                    <LearningPathLineIcon />
                  </div>
                )}
                {duration_total && duration_total !== "0min" && (
                  <>
                    <div className="d-flex justify-content-center gap-1 align-items-center">
                      <LearningPathDurationIcon />
                      {duration_total}
                    </div>
                    <LearningPathLineIcon />
                  </>
                )}

                <div className="d-flex justify-content-center gap-1 align-items-center">
                  <LearningPathLevelIcon />
                  <span>{level || "Intermediate"}</span>
                </div>
                <LearningPathLineIcon />
                <div className="d-flex justify-content-center align-items-center">
                  <span>Released: {formatDate(created_at)}</span>
                </div>
              </div>
            </div>
            <div className="reaction-btn-container2 d-flex gap-4 ">
              {isLearningPath && isLearningPathsDetail ? (
                !isCompleted ? (
                  <div
                    className="mb-0 outline-focussed"
                    tabIndex={0}
                  >
                    {renderCTAButton()}
                  </div>
                ) : null
              ) : !isLesson && inCompletedLessons && !isStandaloneCourse ? (
                <Subscribe
                  courseDetails={userData && userData}
                  isLearningPath={isLearningPath}
                  redirectUrl={
                    (!loggedIn?.email && isLearningPath) ||
                    inCompletedLessons[0] === undefined
                      ? `/academy/learning-paths/${isLearningPath}/${courseUrl}${redirectUrlPath}${
                          hasUrlParameter("start") ? "?start=true" : ""
                        }`
                      : !loggedIn?.email && !isLearningPath
                        ? `/academy/courses/${courseUrl}${redirectUrlPath}${
                            hasUrlParameter("start") ? "?start=true" : ""
                          }`
                        : inCompletedLessons?.length >= 1 && !!isLearningPath
                          ? `/academy/learning-paths/${isLearningPath}/${courseUrl}${inCompletedLessons[0]?.url}${
                              hasUrlParameter("start") ? "?start=true" : ""
                            }`
                          : `/academy/courses/${courseUrl}${inCompletedLessons[0]?.url}${
                              hasUrlParameter("start") ? "?start=true" : ""
                            }`
                  }
                />
              ) : isTextLesson ? (
                (isLast && userData?.progress?.isCompleted && !isLastQuiz) ||
                (!loggedIn && isLast && !isLastQuiz) ? null : (
                  <button
                    disabled={lessonLoading}
                    onClick={
                      !userData?.progress?.isCompleted && isLast
                        ? nextSubmitFunction
                        : nextLessonFunction
                    }
                    tabIndex={0}
                    type="button"
                    className={
                      !userData?.progress?.isCompleted && isLast
                        ? "btn admy-primary-btn"
                        : "admy-primary-btn-outlined"
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        !userData?.progress?.isCompleted && isLast
                          ? nextSubmitFunction
                          : nextLessonFunction;
                      }
                    }}
                  >
                    {(isLast &&
                      !userData?.progress?.isCompleted &&
                      isLastQuiz &&
                      loggedIn) ||
                    (isLast && !userData?.progress?.isCompleted && loggedIn)
                      ? inCompletedLessons?.length === 0
                        ? "Course Completed"
                        : "Finish Course"
                      : lessonLoading
                        ? "Please Wait ..."
                        : "Move To Next Lesson"}
                  </button>
                )
              ) : null}

              <ReactionBtns
                isBookmarked={userData?.isBookmarked}
                handleBookmark={handleBookmarkClick}
                toolTipPosition={"top"}
                isCourse={isCourse}
                isLearningPath={isLearningPath}
                isLearningPathsDetail={isLearningPathsDetail}
              />
            </div>
          </div>
        </>
      ) : !isSticky ? (
        <>
          {" "}
          {!close && !isCourseDetailsPage && (
            <div
              aria-label={`${close ? "Open" : "Close"} navigation menu`}
              role="button"
              tabIndex={0}
            >
              <Tooltip
                text={`${isMobile || isTablet ? "Close" : "Open"} menu`}
                position="right"
              >
                <span
                  className="material-symbols-outlined"
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      document
                        .querySelector(".left-side-nav")
                        .classList.remove("overflowx-hidden");
                      document
                        .querySelector("iframe")
                        .classList.remove("text-lesson-iframe");
                    }
                    setClose(!close);
                  }}
                  role="button"
                >
                  expand_circle_right
                </span>
              </Tooltip>
            </div>
          )}{" "}
          <div className={styles.main_container}>
            <div className={styles.title_container}>
              <h1 className={styles.title}>
                {learningPathwayDetails?.headline || courseDetails?.title}
              </h1>
              <div className="d-flex w-fit flex-wrap gap-2 align-items-center justify-content-between fs-12 fw-normal text-gray  ">
                {!learningPathwayDetails && term && (
                  <div className="d-flex w-fit gap-2 align-items-center justify-content-between fs-12 fw-normal text-gray ">
                    <div className="d-flex justify-content-center gap-1 align-items-center text-capitalize">
                      <LearningPathVideoIcon />
                      {isLesson ? "Lesson" : term}
                    </div>
                    <LearningPathLineIcon />
                  </div>
                )}
                {duration_total && duration_total !== "0min" && (
                  <>
                    <div className="d-flex justify-content-center gap-1 align-items-center">
                      <LearningPathDurationIcon />
                      {duration_total}
                    </div>
                    <LearningPathLineIcon />
                  </>
                )}

                <div className="d-flex justify-content-center gap-1 align-items-center">
                  <LearningPathLevelIcon />
                  <span>{level || "Intermediate"}</span>
                </div>
                <LearningPathLineIcon />
                <div className="d-flex justify-content-center align-items-center">
                  <span>Released: {formatDate(created_at)}</span>
                </div>
              </div>
            </div>
            <div className="reaction-btn-container2 d-flex gap-4 ">
              <ReactionBtns
                isBookmarked={userData?.isBookmarked}
                handleBookmark={handleBookmarkClick}
                toolTipPosition={isSticky ? "bottom" : "top"}
                isCourse={isCourse}
                isLearningPath={isLearningPath}
                isLearningPathsDetail={isLearningPathsDetail}
              />

              {isLearningPath && isLearningPathsDetail ? (
                !isCompleted ? (
                  <div
                    className="mb-0 outline-focussed "
                    tabIndex={0}
                  >
                    {renderCTAButton()}
                  </div>
                ) : null
              ) : !isLesson && inCompletedLessons && !isStandaloneCourse ? (
                <Subscribe
                  isLearningPath={isLearningPath}
                  courseDetails={userData && userData}
                  redirectUrl={
                    (!loggedIn?.email && isLearningPath) ||
                    inCompletedLessons[0] === undefined
                      ? `/academy/learning-paths/${isLearningPath}/${courseUrl}${redirectUrlPath}${
                          hasUrlParameter("start") ? "?start=true" : ""
                        }`
                      : !loggedIn?.email && !isLearningPath
                        ? `/academy/courses/${courseUrl}${redirectUrlPath}${
                            hasUrlParameter("start") ? "?start=true" : ""
                          }`
                        : inCompletedLessons?.length >= 1 && !!isLearningPath
                          ? `/academy/learning-paths/${isLearningPath}/${courseUrl}${inCompletedLessons[0]?.url}${
                              hasUrlParameter("start") ? "?start=true" : ""
                            }`
                          : `/academy/courses/${courseUrl}${inCompletedLessons[0]?.url}${
                              hasUrlParameter("start") ? "?start=true" : ""
                            }`
                  }
                />
              ) : isTextLesson ? (
                (isLast && userData?.progress?.isCompleted && !isLastQuiz) ||
                (!loggedIn && isLast && !isLastQuiz) ? null : (
                  <button
                    disabled={lessonLoading}
                    tabIndex={0}
                    onClick={
                      !userData?.progress?.isCompleted && isLast
                        ? nextSubmitFunction
                        : nextLessonFunction
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        !userData?.progress?.isCompleted && isLast
                          ? nextSubmitFunction
                          : nextLessonFunction;
                      }
                    }}
                    type="button"
                    className={
                      !userData?.progress?.isCompleted && isLast
                        ? "btn admy-primary-btn outline-focussed"
                        : "admy-primary-btn-outlined outline-focussed"
                    }
                  >
                    {(isLast &&
                      !userData?.progress?.isCompleted &&
                      isLastQuiz &&
                      loggedIn) ||
                    (isLast && !userData?.progress?.isCompleted && loggedIn)
                      ? inCompletedLessons?.length === 0
                        ? "Course Completed"
                        : "Finish Course"
                      : lessonLoading
                        ? "Please Wait ..."
                        : "Move To Next Lesson"}
                  </button>
                )
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <>
          {!close && !isCourseDetailsPage && (
            <div
              aria-label={`${close ? "Open" : "Close"} navigation menu`}
              role="button"
              tabIndex={0}
              className="outline-focussed"
            >
              <Tooltip
                text={`${isMobile || isTablet ? "Close" : "Open"} menu`}
                position="right"
              >
                <span
                  className="material-symbols-outlined"
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      document
                        .querySelector(".left-side-nav")
                        .classList.remove("overflowx-hidden");
                      document
                        .querySelector("iframe")
                        .classList.remove("text-lesson-iframe");
                    }
                    setClose(!close);
                  }}
                  role="button"
                >
                  expand_circle_right
                </span>
              </Tooltip>
            </div>
          )}
          <div className={styles.main_container_sticky}>
            <div className={styles.title_container}>
              <h1 className={styles.title}>
                {learningPathwayDetails?.headline || courseDetails?.title}
              </h1>
              <span className={styles.subtitle}>
                {isCourse ? "" : " LEARNING PATH"}
              </span>
            </div>

            <div className="reaction-btn-container2  d-flex gap-4">
              <ReactionBtns
                isBookmarked={userData?.isBookmarked}
                handleBookmark={handleBookmarkClick}
                toolTipPosition={isSticky ? "bottom" : "top"}
                isCourse={isCourse}
                isLearningPath={isLearningPath}
                isLearningPathsDetail={isLearningPathsDetail}
              />

              {isLearningPath && !isCourse ? (
                !isCompleted ? (
                  <div
                    className="mb-0 outline-focussed"
                    tabIndex={0}
                  >
                    {renderCTAButton()}
                  </div>
                ) : null
              ) : !isLesson && inCompletedLessons && !isStandaloneCourse ? (
                <Subscribe
                  courseDetails={userData}
                  redirectUrl={
                    (!loggedIn?.email && isLearningPath) ||
                    inCompletedLessons[0] === undefined
                      ? `/academy/learning-paths/${isLearningPath}/${courseUrl}${redirectUrlPath}${
                          hasUrlParameter("start") ? "?start=true" : ""
                        }`
                      : !loggedIn?.email && !isLearningPath
                        ? `/academy/courses/${courseUrl}${redirectUrlPath}${
                            hasUrlParameter("start") ? "?start=true" : ""
                          }`
                        : inCompletedLessons?.length >= 1 && !!isLearningPath
                          ? `/academy/learning-paths/${isLearningPath}/${courseUrl}${inCompletedLessons[0]?.url}${
                              hasUrlParameter("start") ? "?start=true" : ""
                            }`
                          : `/academy/courses/${courseUrl}${inCompletedLessons[0]?.url}${
                              hasUrlParameter("start") ? "?start=true" : ""
                            }`
                  }
                />
              ) : isTextLesson ? (
                (isLast && userData?.progress?.isCompleted && !isLastQuiz) ||
                (!loggedIn && isLast && !isLastQuiz) ? null : (
                  <button
                    disabled={lessonLoading}
                    onClick={
                      !userData?.progress?.isCompleted && isLast
                        ? nextSubmitFunction
                        : nextLessonFunction
                    }
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        !userData?.progress?.isCompleted && isLast
                          ? nextSubmitFunction
                          : nextLessonFunction;
                      }
                    }}
                    type="button"
                    className={
                      !userData?.progress?.isCompleted && isLast
                        ? "btn admy-primary-btn outline-focussed"
                        : "admy-primary-btn-outlined outline-focussed"
                    }
                  >
                    {(isLast &&
                      !userData?.progress?.isCompleted &&
                      isLastQuiz &&
                      loggedIn) ||
                    (isLast && !userData?.progress?.isCompleted && loggedIn)
                      ? inCompletedLessons?.length === 0
                        ? "Course Completed"
                        : "Finish Course"
                      : lessonLoading
                        ? "Please Wait ..."
                        : "Move To Next Lesson"}
                  </button>
                )
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DynamicStickyHeader;
